import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import {
  Autocomplete,
  Badge,
  Button,
  FormControl,
  Grid,
  InputAdornment,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ColumnsPanelTrigger,
  FilterPanelTrigger,
  GridFilterListIcon,
  GridViewColumnIcon,
  gridFilterModelSelector,
  Toolbar,
  useGridApiContext,
} from '@mui/x-data-grid';
import { useGridSelector } from '@mui/x-data-grid/internals';
import type { ReactElement } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import {
  AddBtnProps,
  ColumnsTriggerBtnProps,
  DeleteBtnProps,
  FilterTriggerBtnProps,
  ICustomToolbarProps,
  IToolBarConfig,
  RecordsPerPageProps,
  RefreshBtnProps,
  SearchInputProps,
} from '../../../types';
import { ECommonDialogType, ECustomToolBar, TableHeaders } from '../../data/Constants';
import { openDialog } from '../../store/states/DialogSlice';

/**
 * Renders an `Autocomplete`-wrapped text input with a leading search icon and a tooltip
 * indicating the searchable fields. It is controlled via `value` and dispatches changes
 * through the provided `onChange` callback.
 *
 * - Controlled input: `value` reflects current search text
 * - Guidance: `tooltip` is shown to inform searchable columns
 * - UX: Adds a start adornment with a search icon
 */
const SearchInput = ({ value, tooltip, onChange }: SearchInputProps): ReactElement => (
  <Autocomplete
    id="product-search-box"
    clearOnBlur
    freeSolo
    value={value}
    options={[]}
    className="search-bar"
    renderInput={(params): ReactElement => (
      <Tooltip title={tooltip} arrow>
        <TextField
          {...params}
          className="search-inner-text"
          placeholder={tooltip}
          sx={{ pl: 2 }}
          slotProps={{
            ...params.slotProps,
            input: {
              ...params.slotProps.input,
              startAdornment: (
                <InputAdornment position="end">
                  <SearchIcon />
                </InputAdornment>
              ),
            },
          }}
        />
      </Tooltip>
    )}
    onChange={(_event, newVal): void => {
      onChange(newVal ?? '');
    }}
  />
);

// toolbar subcomponents

/**
 * Records-per-page selector.
 *
 * Displays a label and a `Select` listing allowed page-size options. When the selection
 * changes, the `onChange` handler receives the corresponding `SelectChangeEvent<number>`.
 *
 * @param {{label: string, limit: number, options: number[], onChange: (e: SelectChangeEvent<number>) => void}} props
 *        - `label`: UI label to display before the selector
 *        - `limit`: current selected page size
 *        - `options`: available page-size choices
 *        - `onChange`: handler for selection change
 * @returns {ReactElement} Records-per-page control.
 */
const RecordsPerPage = ({ label, limit, options, onChange }: RecordsPerPageProps): ReactElement => (
  <>
    <Typography sx={{ mr: 1, ml: 1 }}>{label}:</Typography>
    <FormControl sx={{ minWidth: 'max-content' }}>
      <Select
        value={limit}
        onChange={onChange}
        displayEmpty
        inputProps={{ 'aria-label': 'Without label' }}
        sx={{ height: '36px', lineHeight: '36px' }}
      >
        {options.map((option: number) => (
          <MenuItem key={option} value={option}>
            {option}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  </>
);

/**
 * Columns panel trigger.
 *
 * Shows a tooltip and renders the DataGrid `ColumnsPanelTrigger` with a columns icon.
 * Clicking the trigger opens the grid's columns panel.
 *
 * @param {{title: string}} props - Tooltip title for the trigger.
 * @returns {ReactElement} Columns panel trigger button.
 */
const ColumnsTriggerBtn = ({ title }: ColumnsTriggerBtnProps): ReactElement => (
  <Tooltip title={title}>
    <ColumnsPanelTrigger className="columnsPanelTrigger">
      <GridViewColumnIcon fontSize="small" />
    </ColumnsPanelTrigger>
  </Tooltip>
);

/**
 * Filter panel trigger.
 *
 * Shows a tooltip and renders the DataGrid `FilterPanelTrigger` with a badge indicator
 * when filters are active. `filterCount` controls the badge visibility.
 *
 * @param {{title: string, filterCount: number}} props - Tooltip title and active filter count.
 * @returns {ReactElement} Filter panel trigger button.
 */
const FilterTriggerBtn = ({ title, filterCount }: FilterTriggerBtnProps): ReactElement => (
  <Tooltip title={title}>
    <FilterPanelTrigger className="filterPanelTrigger">
      <Badge variant="dot" color="primary" invisible={filterCount === 0}>
        <GridFilterListIcon />
      </Badge>
    </FilterPanelTrigger>
  </Tooltip>
);

/**
 * Refresh button.
 *
 * Invokes `onRefresh` when clicked. Typically resets search and filters and refetches data.
 *
 * @param {{title: string, onRefresh: () => void}} props - Tooltip title and refresh handler.
 * @returns {ReactElement} Refresh button element.
 */
const RefreshBtn = ({ title, onRefresh }: RefreshBtnProps): ReactElement => (
  <Tooltip title={title}>
    <Button
      className="refresh-button"
      color="primary"
      variant="text"
      startIcon={<RefreshIcon />}
      onClick={onRefresh}
    />
  </Tooltip>
);

/**
 * Delete button.
 *
 * Calls `onDelete` to initiate deletion flow (e.g., confirm dialog). Disabled when
 * deleting is not permitted based on current selection.
 *
 * @param {{title: string, onDelete: () => void, disabled: boolean}} props - Label, handler, and disabled state.
 * @returns {ReactElement} Delete button element.
 */
const DeleteBtn = ({ title, onDelete, disabled }: DeleteBtnProps): ReactElement => (
  <Tooltip title={title}>
    <Button
      onClick={onDelete}
      className="delete-button common-btn"
      startIcon={<DeleteIcon />}
      variant="contained"
      color="error"
      disabled={disabled}
      sx={{ mr: 1 }}
    >
      {title}
    </Button>
  </Tooltip>
);

/**
 * Add/New button.
 *
 * Triggers `onAdd` to start creation of a new record or item.
 *
 * @param {{title: string, onAdd: () => void}} props - Button label and click handler.
 * @returns {ReactElement} Add button element.
 */
const AddBtn = ({ title, onAdd }: AddBtnProps): ReactElement => (
  <Tooltip title={title}>
    <Button
      onClick={onAdd}
      className="text-capitalize common-btn"
      startIcon={<AddIcon />}
      variant="contained"
      color="primary"
    >
      {title}
    </Button>
  </Tooltip>
);

export const CustomToolbar = (props: ICustomToolbarProps): ReactElement => {
  const {
    tableType,
    limit,
    page,
    searchCode,
    setLimit,
    setPage,
    setSearchCode,
    fetchData,
    setFilterModel,
    setLoading,
    selectionModel,
    handleClickDelete,
    handleClickNew,
    customFilterPanel,
    checkIfDeleteButtonIsDisabled,
    requestPayload,
  } = props;

  const [isReset, setIsReset] = useState(false);
  const limitOptions = [15, 25, 50, 100];
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const apiRef = useGridApiContext();

  // Get active filter count reactively - only count filters with values
  const filterModel = useGridSelector(apiRef, gridFilterModelSelector);
  const filterCount =
    filterModel?.items?.filter(
      (item) => item.value !== undefined && item.value !== null && item.value !== ''
    ).length || 0;

  const searchFields = TableHeaders[tableType as keyof typeof TableHeaders]
    .filter((header) => header.searchable)
    .map((header) => header.field);

  const searchFieldsMessage = searchFields.map((field) => t('labels.' + field)).join(', ');

  const tools: IToolBarConfig = ECustomToolBar[tableType as keyof typeof ECustomToolBar];

  // Ensure custom filter panel renders as a component to avoid re-invocation
  const CustomFilterPanelComponent = customFilterPanel;

  /**
   * This function triggers when change limit in the records dropdown
   * @param {SelectChangeEvent<number>} event input event
   */
  const handleLimitChange = (event: SelectChangeEvent<number>): void => {
    setLimit(Number(event.target.value));

    //call the fetch api with new page value to get the rows with existing payload
    Object.keys(requestPayload).length > 0
      ? fetchData({ ...requestPayload, page: 1, limit: event.target.value })
      : fetchData({ page: 1, limit: event.target.value, searchCode });

    //reset filter  value on the data table
    setPage(1);
    setFilterModel({ items: [] });
  };

  /**
   * This function triggers when enter any value on the search box
   * @param {string} newVal current input value
   */
  const handleSearch = (newVal: string): void => {
    setLoading(false);
    fetchData({ page: 1, limit, searchCode: newVal.trim() });
    setSearchCode(newVal ?? '');
    setPage(1);
    setFilterModel({ items: [] });
  };

  /**
   * This function triggers when click on the refresh button on the data table
   */
  const handleRefresh = (): void => {
    setIsReset(true);
    Promise.resolve(fetchData({ page: 1, limit, searchCode: '' })).finally(() => setIsReset(false));
    setSearchCode('');
    setPage(1);
    setFilterModel({ items: [] });
  };

  /**
   * This function trigger when click 'Yes' in the confirm delete dialog window
   */
  const handleConfirmDelete = (): void => {
    handleClickDelete({ page, limit, searchCode });
  };

  /**
   * This function triggers when click on the delete button on the data table
   */
  /**
   * Handles delete action: validates selection size and opens appropriate dialog
   * (error when >10 items, confirm otherwise).
   *
   * @returns void
   */
  const handleDelete = (): void => {
    //show the error  dialog if selected rows is greater than the 10
    if (selectionModel.ids.size > 10) {
      dispatch(
        openDialog({
          type: ECommonDialogType.Error,
          data: t('dialogBox.delete10Items'),
        })
      );
    } else {
      //show the confirmation dialog before delete the rows
      dispatch(
        openDialog({
          type: ECommonDialogType.Confirm,
          title: t('dialogBox.confirm', { action: 'Delete' }),
          data: t('messages.feature.confirmDelete'),
          handleClickOk: handleConfirmDelete,
        })
      );
    }
  };
  return (
    <Toolbar className="customToolbar">
      <Grid className="w-100" container spacing={2}>
        <Grid className="d-flex align-center" size={9}>
          {tools.search && (
            <SearchInput value={searchCode} tooltip={searchFieldsMessage} onChange={handleSearch} />
          )}
          {tools.recordsPerPage && (
            <RecordsPerPage
              label={t('labels.recordsPerPage')}
              limit={limit}
              options={limitOptions}
              onChange={(e) => handleLimitChange(e)}
            />
          )}
          {tools.columns && <ColumnsTriggerBtn title={t('labels.columns')} />}
          {tools.filter && (
            <FilterTriggerBtn title={t('labels.filters')} filterCount={filterCount} />
          )}
          {tools.refresh && <RefreshBtn title={t('button.refresh')} onRefresh={handleRefresh} />}
          {tools.customFilterPanel && CustomFilterPanelComponent && (
            <CustomFilterPanelComponent setPage={setPage} limit={limit} isReset={isReset} />
          )}
        </Grid>
        <Grid className="d-flex justify-end align-center" size={3}>
          {tools.delete && (
            <DeleteBtn
              title={t('button.delete')}
              onDelete={handleDelete}
              disabled={checkIfDeleteButtonIsDisabled()}
            />
          )}
          {tools.add && <AddBtn title={t('button.new')} onAdd={handleClickNew} />}
        </Grid>
      </Grid>
    </Toolbar>
  );
};
